import {
  allBroken,
  analectById,
  chooseNext,
  createCombat,
  createInitialProgress,
  difficultyFor,
  endTurn as endTurnCombat,
  gateBroken,
  gateById,
  getRecord,
  makeChallenge,
  nextUnbrokenGate,
  quoteLabel,
  resolveAnswer,
  spendMana,
} from "../engine";
import type {
  Challenge,
  ChapterFilter,
  Combat,
  GateId,
  NextMode,
  ProgressState,
  VirtueId,
} from "../engine";

type Status = "ready" | "resolving" | "won" | "lost";

export type DuelState = Combat & {
  challenge: Challenge | null;
  activeGate: GateId | null;
  selected: string | null;
  result: "correct" | "wrong" | null;
  status: Status;
  hintedOut: string | null; // option culled by 燭照 (hint), once per challenge
};

export interface LogEntry {
  id: number;
  text: string;
  tone: "info" | "hit" | "miss" | "win" | "lose";
}

export interface FxEvent {
  id: number;
  kind: "hit" | "miss" | "combo" | "win" | "lose";
  gate?: GateId;
  amount?: number;
  virtue?: VirtueId;
}

/** The five top-level screens. `duel` is the game itself; the rest frame it. */
export type ViewId = "today" | "path" | "duel" | "map" | "me";

export interface GameState {
  progress: ProgressState;
  duel: DuelState;
  chapter: ChapterFilter;
  mode: NextMode;
  learnMode: boolean;
  justWonId: number | null;
  log: LogEntry[];
  fx: FxEvent | null;
  seq: number;
  view: ViewId;
}

export type Action =
  | { type: "PLAY_GATE"; gate: GateId }
  | { type: "HINT" }
  | { type: "ANSWER"; optionId: string }
  | { type: "CLOSE_CHALLENGE" }
  | { type: "CONTINUE_CHALLENGE"; challenge: Challenge }
  | { type: "CONTINUE_VICTORY"; quoteId: number }
  | { type: "END_TURN" }
  | { type: "NEXT"; mode?: NextMode }
  | { type: "RESTART" }
  | { type: "JUMP_CHAPTER"; chapter: ChapterFilter }
  | { type: "SET_MODE"; mode: NextMode }
  | { type: "TOGGLE_LEARN" }
  | { type: "CLEAR_FX" }
  | { type: "SET_VIEW"; view: ViewId }
  | { type: "START"; mode?: NextMode; chapter?: ChapterFilter; id?: number }
  | { type: "HYDRATE"; progress: ProgressState };

function freshDuel(progress: ProgressState, id: number, now: number = Date.now()): DuelState {
  const combat = createCombat(progress, id, now);
  return {
    ...combat,
    challenge: null,
    activeGate: null,
    selected: null,
    result: null,
    status: allBroken(combat) ? "won" : "ready",
    hintedOut: null,
  };
}

function pushLog(log: LogEntry[], id: number, text: string, tone: LogEntry["tone"]): LogEntry[] {
  return [{ id, text, tone }, ...log].slice(0, 6);
}

export function initGame(progress: ProgressState, learnMode = false): GameState {
  const first = chooseNext(progress, { mode: "auto" });
  return {
    progress,
    duel: freshDuel(progress, first.id),
    chapter: "all",
    mode: "auto",
    learnMode,
    justWonId: null,
    log: [{ id: 1, text: "開局：選一張攻勢卡，破門立論。", tone: "info" }],
    fx: null,
    seq: 2,
    // The battle theatre is the product's visual identity. The framing views
    // remain one tap away, but a fresh visit should open on the 738d999c-style
    // arena rather than a dashboard summary.
    view: "duel",
  };
}

export function reducer(state: GameState, action: Action): GameState {
  const { duel, progress } = state;

  switch (action.type) {
    case "PLAY_GATE": {
      if (duel.status === "won" || duel.status === "lost") return state;
      if (gateBroken(duel, action.gate)) return state;
      const analect = analectById.get(duel.analectId);
      if (!analect) return state;
      const spent = spendMana(duel, gateById(action.gate).mana);
      // Attempts count re-seeds the challenge: a retried gate gets a fresh
      // variant instead of the same options in the same order.
      const variant = getRecord(progress, duel.analectId).gates[action.gate]?.attempts ?? 0;
      const challenge = makeChallenge(analect, action.gate, difficultyFor(progress, analect), variant);
      return {
        ...state,
        duel: {
          ...duel,
          ...spent,
          challenge,
          activeGate: action.gate,
          selected: null,
          result: null,
          status: "resolving",
          hintedOut: null,
        },
      };
    }

    case "HINT": {
      if (!duel.challenge || duel.selected || duel.hintedOut) return state;
      const culled = duel.challenge.options.find((option) => option.id !== duel.challenge!.answerId);
      if (!culled) return state;
      const spent = spendMana(duel, 1);
      return {
        ...state,
        seq: state.seq + 1,
        log: pushLog(state.log, state.seq, "燭照一分：排除一項曲解，代價 1 明點。", "info"),
        duel: { ...duel, ...spent, hintedOut: culled.id },
      };
    }

    case "ANSWER": {
      if (!duel.challenge || duel.selected) return state;
      if (action.optionId === duel.hintedOut) return state;
      const analect = analectById.get(duel.analectId);
      if (!analect) return state;
      const gate = duel.challenge.gate;
      const correct = action.optionId === duel.challenge.answerId;
      const res = resolveAnswer(duel, progress, gate, correct);

      let seq = state.seq;
      const newCombo = res.combat.combo;
      const fxKind: FxEvent["kind"] =
        res.outcome === "won"
          ? "win"
          : res.outcome === "lost"
            ? "lose"
            : res.outcome === "hit"
              ? newCombo >= 2
                ? "combo"
                : "hit"
              : "miss";
      const fx: FxEvent = { id: seq++, kind: fxKind, gate, amount: res.damage, virtue: res.virtue };

      const label = gateById(gate).label;
      let log = state.log;
      if (res.outcome === "won") {
        log = pushLog(log, seq++, `破關：${quoteLabel(analect)} 四門皆破！`, "win");
      } else if (res.outcome === "lost") {
        log = pushLog(log, seq++, "失利：氣勢已盡，整頓後再破本則。", "lose");
      } else if (correct) {
        log = pushLog(
          log,
          seq++,
          `命中 ${label}，破解 −${res.damage}${newCombo >= 2 ? `（連擊 ×${newCombo}）` : ""}。`,
          "hit",
        );
      } else {
        log = pushLog(log, seq++, `失手 ${label}，氣勢 −${res.damage}。`, "miss");
      }
      if (res.questsJustDone) {
        log = pushLog(log, seq++, `今日修業已成！連續修業 ${res.progress.dayStreak} 天。`, "win");
      }

      const status: Status =
        res.outcome === "won" ? "won" : res.outcome === "lost" ? "lost" : "resolving";
      return {
        ...state,
        progress: res.progress,
        seq,
        fx,
        justWonId: res.outcome === "won" ? analect.id : state.justWonId,
        log,
        duel: {
          ...duel,
          ...res.combat,
          selected: action.optionId,
          result: correct ? "correct" : "wrong",
          status,
        },
      };
    }

    case "CONTINUE_CHALLENGE": {
      if (duel.challenge !== action.challenge || duel.result !== "correct" || state.view !== "duel") return state;
      const closed = reducer(state, { type: "CLOSE_CHALLENGE" });
      return duel.status === "won" ? closed : reducer(closed, { type: "PLAY_GATE", gate: nextUnbrokenGate(duel) });
    }
    case "CONTINUE_VICTORY": {
      if (state.view !== "duel" || duel.status !== "won" || duel.challenge || duel.analectId !== action.quoteId) return state;
      return reducer(state, { type: "NEXT" });
    }
    case "CLOSE_CHALLENGE": {
      const status: Status =
        duel.status === "won" ? "won" : duel.status === "lost" ? "lost" : "ready";
      return {
        ...state,
        duel: { ...duel, challenge: null, activeGate: null, selected: null, result: null, status, hintedOut: null },
      };
    }

    case "END_TURN": {
      if (duel.status === "won" || duel.status === "lost") return state;
      const combat = endTurnCombat(duel);
      return {
        ...state,
        seq: state.seq + 1,
        log: pushLog(state.log, state.seq, `第 ${combat.round} 回合，明點補滿。`, "info"),
        duel: { ...duel, ...combat },
      };
    }

    case "RESTART": {
      return {
        ...state,
        seq: state.seq + 1,
        justWonId: null,
        log: pushLog(state.log, state.seq, "重整旗鼓，再破本則。", "info"),
        duel: freshDuel(progress, duel.analectId),
      };
    }

    case "NEXT": {
      const next = chooseNext(progress, {
        currentId: duel.analectId,
        chapter: state.chapter,
        mode: action.mode ?? state.mode,
      });
      return {
        ...state,
        seq: state.seq + 1,
        justWonId: null,
        log: pushLog(state.log, state.seq, `換題：進入 ${quoteLabel(next)}。`, "info"),
        duel: freshDuel(progress, next.id),
      };
    }

    case "JUMP_CHAPTER": {
      const next = chooseNext(progress, {
        currentId: duel.analectId,
        chapter: action.chapter,
        mode: state.mode,
      });
      return {
        ...state,
        chapter: action.chapter,
        seq: state.seq + 1,
        justWonId: null,
        log: pushLog(state.log, state.seq, `轉場：${quoteLabel(next)}。`, "info"),
        duel: freshDuel(progress, next.id),
      };
    }

    case "SET_MODE":
      return { ...state, mode: action.mode };

    case "TOGGLE_LEARN":
      return { ...state, learnMode: !state.learnMode };

    case "CLEAR_FX":
      return { ...state, fx: null };

    case "SET_VIEW":
      return { ...state, view: action.view };

    /**
     * The one entry point into study from every framing screen: pick the
     * passage, open the arena, and immediately play the first open gate so a
     * student never lands on a board with nothing happening.
     */
    case "START": {
      const chapter = action.chapter ?? state.chapter;
      const target =
        action.id !== undefined
          ? analectById.get(action.id)
          : chooseNext(progress, { chapter, mode: action.mode ?? state.mode });
      const next = target ?? chooseNext(progress, { chapter, mode: action.mode ?? state.mode });
      return {
        ...state,
        view: "duel",
        chapter,
        mode: action.mode ?? state.mode,
        justWonId: null,
        seq: state.seq + 1,
        log: pushLog(state.log, state.seq, `入題：${quoteLabel(next)}。`, "info"),
        duel: freshDuel(progress, next.id),
      };
    }

    case "HYDRATE":
      return initGame(action.progress ?? createInitialProgress(), state.learnMode);

    default:
      return state;
  }
}
